import type { ReconciliationWarnings } from './reconciliation'

export type ChargeReconciliationStatusValue = 'needs_attention' | 'reconciled'

export type ChargeEventStatus =
  | 'DONE'
  | 'MISSED'
  | 'CANCELLED'
  | 'ONGOING'
  | 'OVERDUE'

export type ChargeRateType = 'HOURLY' | 'FIXED'

export enum ChargeReconciliationChargeBy {
  PLANNED_TIME = 'planned_time',
  DONT_CHARGE = 'dont_charge',
  CUSTOM_AMOUNT = 'custom_amount'
}

export interface ChargeReconciliationTime {
  startTime: string
  endTime: string
  durationMinutes: number
}

export interface ChargeReconciliationTeamMember {
  id: string
  name: string
  avatarUrl?: string | null
}

export interface ChargeReconciliationFundingType {
  id: string
  name: string
}

export interface ChargeReconciliationServiceUser {
  id: string
  name: string
  avatarUrl: string | null
}

export interface ChargeReconciliationEventType {
  id: string
  name: string
}

export interface ChargeReconciliationRow {
  rotaInstanceId: string
  startTimeIso: string
  status: ChargeReconciliationStatusValue
  date: string
  fundingType: ChargeReconciliationFundingType | null
  serviceUser: ChargeReconciliationServiceUser | null
  teamMembers: ChargeReconciliationTeamMember[]
  eventType: ChargeReconciliationEventType
  plannedTime: ChargeReconciliationTime
  // Not shown as its own column — used to pre-fill "Charged time" when a
  // custom charge amount is set (see RECONCILIATION_CUSTOM_CHARGE_AMOUNT_BODY_2).
  actualTime: ChargeReconciliationTime | null
  eventStatus: ChargeEventStatus
  warningCount: number
  rawWarnings: ReconciliationWarnings | null
  chargedTime: ChargeReconciliationTime | null
  chargeRate: number | null
  chargeRateType: ChargeRateType | null
  isCustomInstanceChargeRate: boolean | null
  chargeAmount: number | null
  chargeBy: ChargeReconciliationChargeBy | null
  dontChargeReason: string | null
  cancelledBy: string | null
  cancelledReason: string | null
  cancelledFinancialImpact: string | null
  cancelledPreventable: boolean | null
  cancelledNotes: string | null
}

export type ChargeReconciliationSortField =
  | 'status'
  | 'date'
  | 'fundingType'
  | 'serviceUser'
  | 'teamMembers'
  | 'eventType'
  | 'plannedTime'
  | 'eventStatus'
  | 'warningCount'
  | 'chargedTime'
  | 'chargeRate'
  | 'chargeAmount'

export interface ChargeReconciliationFilters {
  startTime: string
  endTime: string
  status?: ChargeReconciliationStatusValue
  fundingTypeIds?: string[]
  serviceUserIds?: string[]
  eventTypeIds?: string[]
  eventStatuses?: ChargeEventStatus[]
  warnings?: string[]
  sortField?: ChargeReconciliationSortField
  sortOrder?: 'asc' | 'desc'
  page?: number
  pageSize?: number
}

export interface UpdateChargeReconciliationPayload {
  chargeStatus: ChargeReconciliationStatusValue
  chargeBy?: ChargeReconciliationChargeBy
  customChargeAmount?: string
  dontChargeReason?: string
}

export interface BulkUpdateChargeByPayload {
  rotaInstanceIds: string[]
  chargeBy: ChargeReconciliationChargeBy.PLANNED_TIME
}
